import React from "react";
import { Stack, Group, Text, Badge } from "@mantine/core";

const EXPENSE_TYPE_LABELS = {
  BACKEND_PERCENTAGE: "Backend Percentage",
  BACKEND_FIXED_AMOUNT: "Backend Fixed Amount",
  FRONTEND_RECURRING: "Frontend Recurring",
};

const RECURRING_SCHEDULE_LABELS = {
  WEEKLY: "Weekly",
  BIWEEKLY: "Biweekly",
  MONTHLY: "Monthly",
};

const DAY_OF_WEEK_LABELS = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

const formatCurrency = value =>
  value == null
    ? "N/A"
    : `$${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatPercent = value => (value == null ? "N/A" : `${value}%`);

const Detail = ({ label, value }) => (
  <Group gap="xs">
    <Text size="sm" c="dimmed">
      {label}:
    </Text>
    <Text size="sm">{value}</Text>
  </Group>
);

// document_text comes back as a related object ({ document_text, ... }) but may
// also be a plain string; normalize to the underlying text.
const getDocumentText = value =>
  value && typeof value === "object" ? value.document_text : value;

// Read-only display of an expense item, matching the main page layout.
function ExpenseDisplay({ item }) {
  if (!item) return null;

  const documentText = getDocumentText(item.document_text);

  return (
    <Stack gap="xs">
      <Group gap="xs" align="center">
        <Text size="sm" fw={600}>
          {item.name}
        </Text>
        {item.expense_type_category && (
          <Badge variant="light">
            {EXPENSE_TYPE_LABELS[item.expense_type_category] ??
              item.expense_type_category}
          </Badge>
        )}
      </Group>
      {documentText && (
        <Text size="sm" c="#444" style={{ whiteSpace: "pre-wrap" }}>
          {documentText}
        </Text>
      )}
      <Group gap="xl">
        {item.backend_percentage != null && (
          <Detail
            label="Backend Percentage"
            value={formatPercent(item.backend_percentage)}
          />
        )}
        {item.backend_fixed_amount != null && (
          <Detail
            label="Backend Fixed Amount"
            value={formatCurrency(item.backend_fixed_amount)}
          />
        )}
        {item.recurring_frontend_amount != null && (
          <Detail
            label="Recurring Amount"
            value={formatCurrency(item.recurring_frontend_amount)}
          />
        )}
        {item.recurring_schedule_type != null && (
          <Detail
            label="Schedule"
            value={
              RECURRING_SCHEDULE_LABELS[item.recurring_schedule_type] ??
              item.recurring_schedule_type
            }
          />
        )}
        {item.recurring_day_of_week != null && (
          <Detail
            label="Day of Week"
            value={
              DAY_OF_WEEK_LABELS[item.recurring_day_of_week] ??
              item.recurring_day_of_week
            }
          />
        )}
        {item.recurring_day_of_month != null && (
          <Detail label="Day of Month" value={item.recurring_day_of_month} />
        )}
      </Group>
    </Stack>
  );
}

export default ExpenseDisplay;
